<template>
  <div v-if="isSysAdmin" class="settings-panel design-profiles-section mb-4">
    <h3 class="section-title">{{ $t('groupManagement.designProfiles.title') }}</h3>
    <p class="section-intro">{{ $t('groupManagement.designProfiles.intro') }}</p>

    <b-table v-if="profiles.length" :items="profiles" :fields="columns" small responsive class="mb-3">
      <template #cell(name)="data">
        {{ data.item.name }}
        <b-badge v-if="data.item.is_default" variant="success" class="ml-1">{{ $t('groupManagement.designProfiles.default') }}</b-badge>
      </template>
      <template #cell(values)="data">{{ summary(data.item) }}</template>
      <template #cell(actions)="data">
        <b-button size="sm" class="row-action" @click="edit(data.item)">{{ $t('groupManagement.designProfiles.edit') }}</b-button>
        <b-button v-if="!data.item.is_default" size="sm" class="row-action" @click="makeDefault(data.item)">
          {{ $t('groupManagement.designProfiles.makeDefault') }}
        </b-button>
        <b-button size="sm" class="row-action row-action-danger" @click="remove(data.item)">{{ $t('groupManagement.designProfiles.delete') }}</b-button>
      </template>
    </b-table>

    <h4 class="block-title">{{ $t(draft.profile_id ? 'groupManagement.designProfiles.editTitle' : 'groupManagement.designProfiles.newTitle') }}</h4>
    <b-form @submit.prevent="save">
      <b-form-group :label="$t('groupManagement.designProfiles.name')">
        <b-form-input v-model="draft.name" maxlength="60" />
      </b-form-group>
      <b-form-group v-for="field in fields" :key="field" :label="$t(`groupManagement.designProfiles.fields.${field}.label`)">
        <b-form-select v-model="draft[field]" :options="options(field)" />
      </b-form-group>
      <b-form-group :label="$t('groupManagement.designProfiles.freeText')" :description="`${draft.free_text.length} / ${maxFreeText}`">
        <b-form-textarea v-model="draft.free_text" :maxlength="maxFreeText" rows="3" />
      </b-form-group>
      <b-button type="submit" variant="primary" size="sm" :disabled="!draft.name.trim()">{{ $t('groupManagement.designProfiles.save') }}</b-button>
      <b-button v-if="draft.profile_id" size="sm" class="ml-2" @click="newDraft">{{ $t('buttons.cancel') }}</b-button>
      <p v-if="error" class="text-danger mt-2 mb-0">{{ error }}</p>
    </b-form>
  </div>
</template>

<script>
import GetDesignProfiles from '@/graphQlQueries/queries/getDesignProfiles';
import InsertDesignProfile from '@/graphQlQueries/mutations/insertDesignProfile';
import UpdateDesignProfile from '@/graphQlQueries/mutations/updateDesignProfile';
import DeleteDesignProfile from '@/graphQlQueries/mutations/deleteDesignProfile';
import SetDefaultDesignProfile from '@/graphQlQueries/mutations/setDefaultDesignProfile';
import VALUES from './designProfileValues';

const FIELDS = Object.keys(VALUES);
/** A new profile starts neutral (spec 2026-10-13 step 2 part B, data). */
const empty = () => ({
  profile_id: null, name: '', headings: 'strong', text_on_image: 'sparse', color_surfaces: 'some', image_weight: 'balanced', free_text: '',
});

export default {
  name: 'DesignProfilesSection',
  props: {
    groupId: { type: Number, required: true },
    isSysAdmin: { type: Boolean, default: false },
  },
  data() {
    return {
      profiles: [], draft: empty(), error: '', fields: FIELDS, maxFreeText: 600,
    };
  },
  computed: {
    columns() {
      return [
        { key: 'name', label: this.$t('groupManagement.designProfiles.name') },
        { key: 'values', label: '' },
        { key: 'actions', label: '', tdClass: 'text-nowrap text-right' },
      ];
    },
  },
  watch: {
    groupId() {
      this.newDraft();
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      if (!this.isSysAdmin) return;
      const result = await this.$apollo.query({ query: GetDesignProfiles, variables: { groupId: this.groupId }, fetchPolicy: 'network-only' });
      this.profiles = result.data.design_profiles || [];
    },
    options(field) {
      return VALUES[field].map((v) => ({ value: v, text: this.$t(`groupManagement.designProfiles.fields.${field}.values.${v}.long`) }));
    },
    summary(item) {
      return FIELDS.map((f) => this.$t(`groupManagement.designProfiles.fields.${f}.values.${item[f]}.short`)).join(' · ');
    },
    newDraft() {
      this.draft = empty();
      this.error = '';
    },
    edit(item) {
      this.draft = { ...empty(), ...item };
      this.error = '';
    },
    async save() {
      const d = this.draft;
      const fields = {
        name: d.name.trim(), headings: d.headings, text_on_image: d.text_on_image, color_surfaces: d.color_surfaces, image_weight: d.image_weight, free_text: d.free_text,
      };
      this.error = '';
      try {
        if (d.profile_id) {
          await this.$apollo.mutate({ mutation: UpdateDesignProfile, variables: { id: d.profile_id, set: fields } });
        } else {
          // The first profile of an account becomes its default.
          await this.$apollo.mutate({
            mutation: InsertDesignProfile,
            variables: { object: { group_id: this.groupId, is_default: this.profiles.length === 0, ...fields } },
          });
        }
      } catch (err) {
        console.error('[DesignProfiles]', err);
        this.error = this.$t('groupManagement.designProfiles.error');
        return;
      }
      this.newDraft();
      await this.load();
    },
    /** One write from a row button; a failure shows the same error line as the form. */
    async write(options) {
      this.error = '';
      try {
        await this.$apollo.mutate(options);
        return true;
      } catch (err) {
        console.error('[DesignProfiles]', err);
        this.error = this.$t('groupManagement.designProfiles.error');
        return false;
      }
    },
    async makeDefault(item) {
      if (await this.write({ mutation: SetDefaultDesignProfile, variables: { groupId: this.groupId, id: item.profile_id } })) await this.load();
    },
    async remove(item) {
      const ok = await this.$bvModal.msgBoxConfirm(
        this.$t('groupManagement.designProfiles.deleteConfirm', { name: item.name }),
        {
          okTitle: this.$t('groupManagement.designProfiles.delete'), okVariant: 'danger', cancelTitle: this.$t('buttons.cancel'), centered: true,
        },
      ).catch(() => false);
      if (!ok) return;
      if (!(await this.write({ mutation: DeleteDesignProfile, variables: { id: item.profile_id } }))) return;
      if (this.draft.profile_id === item.profile_id) this.newDraft();
      await this.load();
    },
  },
};
</script>

<style scoped lang="scss">
@import './settingsPanel';

// Same quiet row buttons as CustomRedirectsSection.
.row-action {
  color: $webmag-light-grey;
  background: transparent;
  border: none;
  padding: 2px 8px;

  &:hover,
  &:focus {
    color: #fff;
    background: transparent;
    box-shadow: none;
  }
}

.row-action-danger:hover,
.row-action-danger:focus {
  color: #f08080;
}
</style>
